import { Controller } from "stimulus";

export default class extends Controller {
  static get targets() {
    return ["input", "dropdown", "hiddenInput"];
  }

  connect() {
    console.log("Typeahead controller connected");
    this.selectedIndex = -1;
    this.isSettingValue = false; // Flag to prevent search during programmatic value setting

    // In Stimulus 1.1.1, targets might not be available immediately
    if (this.hasDropdownTarget) {
      this.hideDropdown();
      console.log("Dropdown target found and hidden");
    } else {
      console.log("No dropdown target found");
    }

    // Get category and URL from data attributes (no values API in 1.1.1)
    this.category = this.data.get("category") || "";
    this.searchUrl = this.data.get("url") || "/patient_care_form_codes/search";

    console.log("Category:", this.category);
    console.log("Search URL:", this.searchUrl);
  }

  search() {
    console.log("Search method called");

    // Skip search if we're programmatically setting a value
    if (this.isSettingValue) {
      console.log("Skipping search - programmatically setting value");
      return;
    }

    if (!this.hasInputTarget) {
      console.log("No input target found");
      return;
    }

    const query = this.inputTarget.value.trim();
    console.log("Search query:", query);

    if (query.length < 2) {
      console.log("Query too short, hiding dropdown");
      this.hideDropdown();
      return;
    }

    this.selectedIndex = -1;
    this.fetchResults(query);
  }

  async fetchResults(query) {
    console.log("Fetching results for:", query);
    try {
      const url = `${this.searchUrl}?category=${encodeURIComponent(
        this.category
      )}&q=${encodeURIComponent(query)}`;
      console.log("Making request to:", url);

      const response = await fetch(url);
      const data = await response.json();

      console.log("Response data:", data);

      if (data.success) {
        this.displayResults(data.codes);
      }
    } catch (error) {
      console.error("Type-ahead search failed:", error);
    }
  }

  displayResults(codes) {
    console.log("Displaying results:", codes);

    if (!this.hasDropdownTarget) {
      console.log("No dropdown target to display results");
      return;
    }

    this.dropdownTarget.innerHTML = "";

    if (codes.length === 0) {
      const noResults = document.createElement("div");
      noResults.className = "typeahead-item text-muted";
      noResults.textContent = "No results found";
      this.dropdownTarget.appendChild(noResults);
      this.showDropdown();
      return;
    }

    codes.forEach((code, index) => {
      const item = document.createElement("div");
      item.className = "typeahead-item";
      item.textContent = code.name;
      item.dataset.value = code.value;
      item.dataset.index = index;

      // Prevent the input from losing focus when clicking on dropdown item
      item.addEventListener("mousedown", (e) => {
        e.preventDefault();
        console.log("Mousedown on item:", code.name);
      });

      item.addEventListener("click", (e) => {
        e.preventDefault();
        console.log("Click on item:", code.name);
        this.selectItem(code.value, code.name);
      });

      item.addEventListener("mouseenter", () => this.setActiveItem(index));

      this.dropdownTarget.appendChild(item);
    });

    this.showDropdown();
  }

  selectItem(value, displayText) {
    if (!this.hasInputTarget) {
      console.log("No input target found in selectItem");
      return;
    }

    // Only set value if the input field is visible (not in a hidden parent)
    const inputElement = this.inputTarget;
    console.log("Input element:", inputElement);
    console.log("Input element value before:", inputElement.value);
    console.log("Input element classes:", inputElement.className);
    console.log("Input element name:", inputElement.name);

    const isVisible = inputElement.offsetParent !== null;
    console.log("Is input visible:", isVisible);
    console.log("Input offsetParent:", inputElement.offsetParent);

    if (!isVisible) {
      console.log("Typeahead input is hidden, skipping setValue");
      return;
    }

    // Set flag to prevent search during value setting
    this.isSettingValue = true;

    console.log("Setting value to:", displayText);
    inputElement.value = displayText;
    // Also set the HTML attribute to ensure form serialization works
    inputElement.setAttribute("value", displayText);
    console.log("Input element value after:", inputElement.value);

    if (this.hasHiddenInputTarget) {
      console.log("Setting hidden input value to:", value);
      this.hiddenInputTarget.value = value;
    }

    // Trigger input and change events so auto-save detects the change
    console.log("Triggering input event");
    inputElement.dispatchEvent(new Event("input", { bubbles: true }));
    console.log("Triggering change event");
    inputElement.dispatchEvent(new Event("change", { bubbles: true }));

    console.log(
      `Typeahead set value: ${displayText} in field: ${inputElement.className}`
    );

    // Reset flag after a short delay to allow events to process
    setTimeout(() => {
      this.isSettingValue = false;
      console.log("Reset isSettingValue flag");
    }, 100);

    this.hideDropdown();
    inputElement.focus();
  }

  keydown(event) {
    if (!this.hasDropdownTarget) return;

    const items = this.dropdownTarget.querySelectorAll(
      ".typeahead-item:not(.text-muted)"
    );

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1);
        this.updateActiveItem();
        break;

      case "ArrowUp":
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.updateActiveItem();
        break;

      case "Enter":
        event.preventDefault();
        if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
          const selectedItem = items[this.selectedIndex];
          this.selectItem(selectedItem.dataset.value, selectedItem.textContent);
        }
        break;

      case "Escape":
        this.hideDropdown();
        break;
    }
  }

  updateActiveItem() {
    if (!this.hasDropdownTarget) return;

    const items = this.dropdownTarget.querySelectorAll(".typeahead-item");
    items.forEach((item, index) => {
      item.classList.toggle("active", index === this.selectedIndex);
    });
  }

  setActiveItem(index) {
    this.selectedIndex = index;
    this.updateActiveItem();
  }

  showDropdown() {
    if (!this.hasDropdownTarget) return;
    console.log("Showing dropdown");
    this.dropdownTarget.style.display = "block";
  }

  hideDropdown() {
    if (!this.hasDropdownTarget) return;
    console.log("Hiding dropdown");
    this.dropdownTarget.style.display = "none";
    this.selectedIndex = -1;
  }

  blur() {
    // Delay hiding to allow click events to fire
    setTimeout(() => {
      this.hideDropdown();
    }, 200);
  }
}
